import 'src/global.css';

// ----------------------------------------------------------------------

import { CacheProvider } from '@emotion/react';
import { ConfigProvider, notification } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { useEffect } from 'react';

import { Router } from 'src/routes/sections';

import { useScrollToTop } from 'src/hooks/use-scroll-to-top';

import { LocalizationProvider } from 'src/locales';
import { I18nProvider } from 'src/locales/i18n-provider';
import { ThemeProvider } from 'src/theme/theme-provider';

import { MotionLazy } from 'src/components/animate/motion-lazy';
import { ProgressBar } from 'src/components/progress-bar';
import { defaultSettings, SettingsDrawer, SettingsProvider } from 'src/components/settings';
import { Snackbar } from 'src/components/snackbar';

import { AuthSocketWrapper } from 'src/auth/components/AuthSocketWrapper';
import { AuthProvider } from 'src/auth/context/jwt';
import { WebSocketProvider } from 'src/contexts/webSocketContext';
import { WebsiteRuntime } from 'src/sections/overview/settings/components/WebsiteRuntime';

import createEmotionCache from './createEmotionCache';
import { useDisableZoom } from './hooks/useDisableZoom';

notification.config({
  duration: 3,
  rtl: false,
  maxCount: 1,
});

const clientSideEmotionCache = createEmotionCache();

export default function App() {
  const [, contextHolder] = notification.useNotification();
  useScrollToTop();
  useDisableZoom();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js').catch(() => undefined);
    }
  }, []);

  return (
    <I18nProvider>
      {contextHolder}
      <CacheProvider value={clientSideEmotionCache}>
        <LocalizationProvider>
          <AuthProvider>
            <WebSocketProvider>
              <AuthSocketWrapper>
                <SettingsProvider settings={defaultSettings}>
                  <ThemeProvider>
                    <ConfigProvider
                      locale={viVN}
                      theme={{
                        token: {
                          colorPrimary: '#06564D',
                          colorInfo: '#00B8D9',
                          colorSuccess: '#22C55E',
                          colorWarning: '#FFAB00',
                          colorError: '#FF5630',
                          colorText: '#1C252E',
                          colorTextSecondary: '#637381',
                          colorBgLayout: '#FFFFFF',
                          colorBgContainer: '#FFFFFF',
                          colorBorder: '#DFE3E8',
                          borderRadius: 8,
                          fontFamily: '"Public Sans", sans-serif',
                        },
                        components: {
                          Button: {
                            controlHeight: 40,
                            borderRadius: 8,
                          },
                          Card: {
                            borderRadiusLG: 8,
                          },
                          Drawer: {
                            paddingLG: 20,
                          },
                          Modal: {
                            borderRadiusLG: 8,
                          },
                          Table: {
                            borderColor: '#DFE3E8',
                            cellPaddingBlock: 13,
                            cellPaddingInline: 16,
                            headerBg: '#F4F6F8',
                            headerColor: '#637381',
                            rowHoverBg: '#F9FAFB',
                          },
                        },
                      }}
                    >
                      <MotionLazy>
                        <Snackbar />
                        <ProgressBar />
                        <SettingsDrawer />
                        <WebsiteRuntime>
                          <Router />
                        </WebsiteRuntime>
                      </MotionLazy>
                    </ConfigProvider>
                  </ThemeProvider>
                </SettingsProvider>
              </AuthSocketWrapper>
            </WebSocketProvider>
          </AuthProvider>
        </LocalizationProvider>
      </CacheProvider>
    </I18nProvider>
  );
}
